/**
 * @file components/MobileAppCta/component.js
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 * @author Avi Craimer [avi.craimer@gmail.com]
 */

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Title from "../Title/component";
import IconLogo from "../Icon/IconLogo";
import IconClose from "../Icon/IconClose";
import LinkWrap from "../LinkWrap/component";
import { isAndroid, isAppleDevice } from "../../utils/user-agent/library";
import {
    storeCtaCloseCookie,
    storeAppDownloadedCookie,
    appDownloadedCookie,
    showMobileAppCta,
} from "../../utils/cookies/library";
import { fetch_audience } from "../../actions/DMP/dispatchers";
import * as CONSTANTS from "./constants";
import {
    containsAudienceSegment,
    publishClickedEvent,
    buttonLink,
} from "./utils";
import styles from "../Title/styles/themes/default.module.scss";
/**
 * Functional component representing the MobileAppCta component.
 *
 * A call to action to download the app for loyal users on mobile devices.
 *
 */

export const MobileAppCta = () => {
    const [slideUp, setSlideUp] = useState(false);
    const [deviceType, setDeviceType] = useState(null);
    const [showCtaCookie, setShowCtaCookie] = useState(true);
    const [appDownloaded, setAppDownloaded] = useState(false);
    const [unmounted, setUnmounted] = useState(false);

    const segments = useSelector((state) => state.dmp.audienceSegments);
    const baseSection = useSelector((state) => state.app.baseSection);
    const pageType = useSelector((state) => state.app.pageType);
    const path = useSelector((state) => state.app.path);
    const contentId = useSelector((state) => state.app.contentId);
    const fixed = useSelector((state) => state.fixed);
    const dispatch = useDispatch();

    useEffect(() => {
        function getDeviceType() {
            let type = null;
            if (isAndroid()) {
                type = "android";
            } else if (isAppleDevice()) {
                type = "iOS";
            }
            return type;
        }

        setDeviceType(getDeviceType());
        setShowCtaCookie(showMobileAppCta());
        setAppDownloaded(appDownloadedCookie());
        dispatch(fetch_audience());
        return () => {
            setUnmounted(true);
        };
    }, []);

    useEffect(() => {
        function animateUpDown(direction) {
            if (direction === "up" && !unmounted) {
                setSlideUp(true);
            } else if (direction === "down" && !unmounted) {
                setSlideUp(false);
            }
        }

        if (pageType === "detail") {
            setTimeout(() => {
                animateUpDown("up");
            }, 1000);
        } else {
            animateUpDown("down");
        }
    }, [pageType, path]);

    const handleCloseClick = () => {
        setSlideUp(false);
        storeCtaCloseCookie();
    };

    const buttonProps = {
        path: path,
        contentId: contentId,
        appDownloaded,
        deviceType,
    };

    if (fixed && fixed.isWebView) return null;

    if (
        deviceType === null ||
        pageType !== "detail" ||
        baseSection !== "news" ||
        !showCtaCookie ||
        !containsAudienceSegment(segments, CONSTANTS.AUDIENCE_SEGMENT)
    ) {
        return null;
    }

    return (
        <div
            role="dialog"
            className={`mobileAppCta ${slideUp ? "slideUp" : ""} ${
                styles.mobileAppCta
            }`}
        >
            <LinkWrap
                url={buttonLink(buttonProps)}
                external={true}
                className="mobileAppCtaLink"
                onClick={() => publishClickedEvent(storeAppDownloadedCookie)}
            >
                <IconLogo />
                <Title level="h2">Read in the app</Title>
                <button className="mobileAppCtaBtn">Open</button>
            </LinkWrap>
            <button
                className="mobileAppCtaClose"
                onClick={() => handleCloseClick()}
            >
                <IconClose />
            </button>
        </div>
    );
};

export default MobileAppCta;
